import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    HiOutlineGlobeAlt, HiOutlineServer, HiOutlineDatabase, HiOutlineLink, HiOutlinePhotograph, HiOutlineMail,
    HiOutlineCreditCard, HiOutlineSparkles, HiOutlineRefresh, HiOutlinePencil, HiOutlineExternalLink, HiCheckCircle,
    HiExclamation, HiXCircle, HiQuestionMarkCircle, HiOutlineLockClosed, HiOutlineBell, HiOutlineCurrencyRupee,
    HiOutlineCalendar, HiOutlineChartBar
} from 'react-icons/hi';
import { FaGoogle } from 'react-icons/fa';
import AuthenticationService from '../service/AuthenticationService';
import MonitorService, { SERVICES_CHANGED } from '../service/MonitorService';
import SidePanel from '../components/SidePanel';
import Toast from '../components/Toast';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { formatRupee } from '../components/Price';
import '../css/admin.css'
import '../css/services.css'

const ICONS = {
    react_hosting: <HiOutlineGlobeAlt />, java_hosting: <HiOutlineServer />, database: <HiOutlineDatabase />,
    domain: <HiOutlineLink />, s3: <HiOutlinePhotograph />, mail: <HiOutlineMail />, razorpay: <HiOutlineCreditCard />,
    google: <FaGoogle />, ai: <HiOutlineSparkles />
}

const STATUS = {
    OK: { label: 'All good', icon: <HiCheckCircle />, tone: 'is-ok' },
    WARNING: { label: 'Needs attention', icon: <HiExclamation />, tone: 'is-warn' },
    DOWN: { label: 'Down', icon: <HiXCircle />, tone: 'is-down' },
    UNKNOWN: { label: 'Not checked', icon: <HiQuestionMarkCircle />, tone: 'is-unknown' }
}

const FILTERS = [{ key: 'all', label: 'All services' }, { key: 'attention', label: 'Needs attention' }]

const PLAN_TYPES = [{ value: 'FREE', label: 'Free' }, { value: 'PAID', label: 'Paid' }, { value: 'TRIAL', label: 'Trial' }]
const CYCLES = [{ value: 'MONTHLY', label: 'Every month' }, { value: 'YEARLY', label: 'Every year' }, { value: 'NONE', label: 'One-time or none' }]

const daysText = (days) => days < 0 ? `${-days} ${days === -1 ? 'day' : 'days'} ago`
    : days === 0 ? 'today' : `in ${days} ${days === 1 ? 'day' : 'days'}`

const formatDate = (iso) => {
    if (!iso) return ''
    const d = new Date(iso + 'T00:00:00')
    return isNaN(d) ? iso : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const ago = (iso) => {
    if (!iso) return 'Not checked yet'
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
    if (isNaN(mins)) return ''
    if (mins < 1) return 'Checked just now'
    if (mins < 60) return `Checked ${mins} min ago`
    if (mins < 60 * 24) return `Checked ${Math.round(mins / 60)} h ago`
    return `Checked ${Math.round(mins / 1440)} d ago`
}

const formFrom = (account, limitFields) => ({
    provider: account.provider || '',
    planName: account.planName || '',
    planType: account.planType || 'FREE',
    cost: account.cost != null ? String(account.cost) : '0',
    currency: account.currency || 'INR',
    billingCycle: account.billingCycle || 'NONE',
    startedOn: account.startedOn || '',
    renewsOn: account.renewsOn || '',
    freeTierEndsOn: account.freeTierEndsOn || '',
    autoRenew: account.autoRenew == null ? '' : String(account.autoRenew),
    limits: Object.fromEntries((limitFields || []).map(f => [f.key,
        account.limits && account.limits[f.key] != null ? String(account.limits[f.key]) : ''])),
    dashboardUrl: account.dashboardUrl || '',
    notes: account.notes || ''
})

const Tile = ({ icon, label, value, sub, tone, loading }) => (
    <div className={'mv-svc-tile ' + tone}>
        <span className='mv-svc-tile-icon' aria-hidden='true'>{icon}</span>
        <span className='mv-svc-tile-label'>{label}</span>
        {loading ? <Skeleton width='60%' height={22} /> : <span className='mv-svc-tile-value'>{value}</span>}
        <span className='mv-svc-tile-sub'>{loading ? ' ' : sub || ' '}</span>
    </div>
)

const Card = ({ card, busy, onRefresh, onEdit }) => {
    const status = STATUS[card.status] || STATUS.UNKNOWN
    const account = card.account || {}
    return (
        <article className={'mv-svc-card ' + status.tone} aria-labelledby={'svc-' + card.key}>
            <header className='mv-svc-head'>
                <span className='mv-svc-icon' aria-hidden='true'>{ICONS[card.key]}</span>
                <span className='mv-svc-title'>
                    <h2 id={'svc-' + card.key}>{card.name}</h2>
                    <span className='mv-svc-provider'>
                        {account.provider || 'Provider not set'}
                        {account.planName && <span className='mv-svc-plan'>{account.planName}</span>}
                    </span>
                </span>
                <span className={'mv-svc-status ' + status.tone}>
                    <span aria-hidden='true'>{status.icon}</span>{status.label}
                </span>
            </header>

            {card.reasons && card.reasons.length > 0 &&
                <ul className='mv-svc-reasons'>{card.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}

            {card.metrics && card.metrics.length > 0 &&
                <ul className='mv-svc-metrics'>
                    {card.metrics.map((m, i) =>
                        <li key={i} className={m.warn ? 'is-warn' : ''}>
                            <span className='mv-svc-metric-label'>{m.label}</span>
                            <span className='mv-svc-metric-value'>{m.value}</span>
                            {m.percent != null &&
                                <span className='mv-svc-bar' role='meter' aria-valuemin={0} aria-valuemax={100}
                                    aria-valuenow={Math.min(100, m.percent)} aria-label={`${m.label}: ${m.percent}% used`}>
                                    <span style={{ width: Math.min(100, Math.max(2, m.percent)) + '%' }} />
                                </span>}
                        </li>)}
                </ul>}

            {card.expiries && card.expiries.length > 0 &&
                <ul className='mv-svc-dates'>
                    {card.expiries.map((e, i) =>
                        <li key={i} className={e.warn ? 'is-warn' : ''}>
                            <HiOutlineCalendar aria-hidden='true' />
                            <span>{e.label}</span>
                            <strong>{formatDate(e.date)}</strong>
                            <small>{daysText(e.daysLeft)}</small>
                        </li>)}
                </ul>}

            {card.facts && card.facts.length > 0 &&
                <dl className='mv-svc-facts'>
                    {card.facts.map((f, i) => <div key={i}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
                </dl>}

            {account.notes && <p className='mv-svc-notes'>{account.notes}</p>}

            <div className='mv-svc-cost'>
                <span>This month{card.usageEstimated ? ' (est.)' : ''}</span>
                <strong>{formatRupee(card.monthInr || 0)}</strong>
                <span className='mv-svc-cost-total'>Since start {formatRupee(card.totalInr || 0)}</span>
            </div>

            <footer className='mv-svc-foot'>
                <span className='mv-svc-checked'>{busy ? 'Checking…' : ago(card.checkedAt)}</span>
                <span className='mv-svc-actions'>
                    {account.dashboardUrl &&
                        <a className='mv-icon-btn' href={account.dashboardUrl} target='_blank' rel='noopener noreferrer'
                            aria-label={`Open the ${card.name} dashboard`} title='Open dashboard'>
                            <HiOutlineExternalLink />
                        </a>}
                    <button type='button' className='mv-icon-btn' onClick={onRefresh} disabled={busy}
                        aria-label={`Check ${card.name} now`} title='Check now'>
                        <HiOutlineRefresh className={busy ? 'mv-spin' : ''} />
                    </button>
                    <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={onEdit}>
                        <HiOutlinePencil aria-hidden='true' /> Edit details
                    </button>
                </span>
            </footer>
        </article>
    )
}

const CardSkeleton = () => (
    <div className='mv-svc-card is-loading' aria-hidden='true'>
        <div className='mv-svc-head'>
            <Skeleton width={40} height={40} radius={12} />
            <span className='mv-svc-title'><Skeleton width='55%' height={16} /><Skeleton width='35%' height={12} /></span>
        </div>
        <Skeleton height={12} /><Skeleton width='80%' height={12} /><Skeleton width='60%' height={12} />
    </div>
)

// Admin-only Services page: status, usage, expiry dates and cost of the outside services the shop runs on.
// Managers see a lock and the API is never called (a 403 would sign them out).
const ServiceMonitor = () => {
    const history = useHistory()
    const isAdmin = AuthenticationService.canManageSettings()
    const [data, setData] = useState(null)
    const [loadError, setLoadError] = useState(false)
    const [filter, setFilter] = useState('all')
    const [busy, setBusy] = useState(null) // 'all' | service key
    const [editing, setEditing] = useState(null) // card
    const [form, setForm] = useState(null)
    const [formError, setFormError] = useState('')
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState('')
    const errorRef = useRef(null)

    // a save error shows at the top of the panel: bring it into view (on phones the sheet is scrolled to Save)
    useEffect(() => {
        if (formError && errorRef.current) {
            errorRef.current.scrollIntoView({ block: 'nearest' })
            errorRef.current.focus({ preventScroll: true })
        }
    }, [formError])

    const show = useCallback((overview) => {
        setData(overview)
        window.dispatchEvent(new CustomEvent(SERVICES_CHANGED, { detail: overview.warnings }))
    }, [])

    useEffect(() => {
        if (!isAdmin) return
        MonitorService.overview()
            .then(res => {
                const overview = res.data.data
                show(overview)
                // never checked (first visit after setup): check now
                if (overview.services.every(s => !s.checkedAt)) refresh(null)
            })
            .catch(() => setLoadError(true))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin])

    const refresh = (key) => {
        setBusy(key || 'all')
        MonitorService.refresh(key)
            .then(res => show(res.data.data))
            .catch(error => setToast(error?.response?.data?.msg || "Couldn't check right now. Please try again."))
            .finally(() => setBusy(null))
    }

    const openEditor = (card) => {
        setEditing(card)
        setForm(formFrom(card.account || {}, card.limitFields))
        setFormError('')
        setToast('')
    }

    const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))
    const setLimit = (key) => (e) => setForm(f => ({ ...f, limits: { ...f.limits, [key]: e.target.value.replace(/[^0-9.]/g, '') } }))

    const save = (e) => {
        e.preventDefault()
        const cost = form.cost.trim() === '' ? 0 : Number(form.cost)
        if (isNaN(cost) || cost < 0) { setFormError('Enter a cost from 0.'); return }
        const url = form.dashboardUrl.trim()
        if (url && !/^https:\/\/\S+$/.test(url)) { setFormError('The dashboard link must start with https://'); return }
        const limits = {}
        Object.entries(form.limits).forEach(([k, v]) => { if (String(v).trim() !== '') limits[k] = Number(v) })
        setSaving(true)
        setFormError('')
        MonitorService.save(editing.key, {
            provider: form.provider.trim(), planName: form.planName.trim(), planType: form.planType, cost,
            currency: form.currency, billingCycle: form.billingCycle, startedOn: form.startedOn, renewsOn: form.renewsOn,
            freeTierEndsOn: form.freeTierEndsOn, autoRenew: form.autoRenew === '' ? null : form.autoRenew === 'true',
            limits, dashboardUrl: url, notes: form.notes.trim()
        })
            .then(res => {
                show(res.data.data)
                setEditing(null)
                setToast('Saved')
            })
            .catch(error => setFormError(error?.response?.data?.msg || "Couldn't save. Please try again."))
            .finally(() => setSaving(false))
    }

    if (!isAdmin) {
        return (
            <main className='mv-page mv-svc'>
                <EmptyState icon={<HiOutlineLockClosed />} title='Only admins can see services'
                    text='Hosting, keys and costs are kept to admins. Ask an admin if something looks wrong.'
                    actionLabel='Back to dashboard' onAction={() => history.push('/dashboard')} />
            </main>
        )
    }

    const services = (data && data.services) || []
    const attention = services.filter(s => s.status === 'WARNING' || s.status === 'DOWN')
    const shown = filter === 'attention' ? attention : services
    const next = data && data.nextExpiry

    return (
        <main className='mv-page mv-svc'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Services</h1>
                    <p className='mv-page-sub'>Hosting, storage, email, payments and AI: usage, limits, dates and costs in one place.</p>
                </div>
                <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={() => refresh(null)} disabled={!!busy || !data}>
                    <HiOutlineRefresh aria-hidden='true' className={busy === 'all' ? 'mv-spin' : ''} />
                    {busy === 'all' ? 'Checking…' : 'Check all now'}
                </button>
            </div>

            <div className='mv-svc-tiles'>
                <Tile icon={<HiOutlineBell />} label='Needs attention' loading={!data}
                    tone={data && data.warnings > 0 ? 'is-warn' : 'is-ok'}
                    value={data ? data.warnings : ''} sub={data ? (data.warnings ? 'See cards below' : 'All fine') : ''} />
                <Tile icon={<HiOutlineCurrencyRupee />} label='This month (est.)' tone='is-primary' loading={!data}
                    value={data ? formatRupee(data.monthInr || 0) : ''} sub={data ? `$1 = ₹${data.usdInr}` : ''} />
                <Tile icon={<HiOutlineChartBar />} label='Spent since start' tone='is-accent' loading={!data}
                    value={data ? formatRupee(data.totalInr || 0) : ''} sub='Estimated' />
                <Tile icon={<HiOutlineCalendar />} label='Next date' loading={!data}
                    tone={next && next.daysLeft <= 30 ? 'is-warn' : 'is-info'}
                    value={next ? daysText(next.daysLeft) : '—'}
                    sub={next ? `${next.service}: ${next.label.toLowerCase()}` : 'No dates yet'} />
            </div>

            <div className='mv-chip-row' role='radiogroup' aria-label='Show'>
                {FILTERS.map(f =>
                    <button key={f.key} type='button' role='radio' aria-checked={filter === f.key}
                        className={'mv-filter-chip' + (filter === f.key ? ' active' : '')} onClick={() => setFilter(f.key)}>
                        {f.label}{f.key === 'attention' && data ? ` (${attention.length})` : ''}
                    </button>)}
            </div>

            {loadError && !data ?
                <EmptyState icon={<HiOutlineServer />} title="Couldn't load services" text='Please check your connection and try again.'
                    actionLabel='Try again' onAction={() => window.location.reload()} /> :
                <div className='mv-svc-grid'>
                    {!data && Array.from({ length: 6 }, (_, i) => <CardSkeleton key={i} />)}
                    {data && shown.length === 0 &&
                        <p className='mv-svc-empty'><HiCheckCircle aria-hidden='true' /> Nothing needs attention right now.</p>}
                    {shown.map(card =>
                        <Card key={card.key} card={card} busy={busy === card.key || busy === 'all'}
                            onRefresh={() => refresh(card.key)} onEdit={() => openEditor(card)} />)}
                </div>}

            <SidePanel open={!!editing} title={editing ? `${editing.name}: details` : ''} busy={saving} onClose={() => setEditing(null)}
                footer={<>
                    <span className='mv-panel-spacer' />
                    <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
                    <button type='submit' form='svc-form' className='mv-btn mv-btn-primary' disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </>}>
                {editing && form &&
                    <form id='svc-form' className='mv-form mv-svc-form' onSubmit={save} noValidate>
                        {formError && <p className='mv-alert mv-alert-error' role='alert' tabIndex={-1} ref={errorRef}>{formError}</p>}
                        <div className='mv-form-grid'>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-provider'>Provider</label>
                                <input id='svc-provider' className='mv-input' maxLength={60} value={form.provider} onChange={set('provider')}
                                    placeholder='e.g. Hostinger' />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-plan'>Plan</label>
                                <input id='svc-plan' className='mv-input' maxLength={60} value={form.planName} onChange={set('planName')}
                                    placeholder='e.g. Starter' />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-type'>Plan type</label>
                                <select id='svc-type' className='mv-select' value={form.planType} onChange={set('planType')}>
                                    {PLAN_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-cycle'>Billed</label>
                                <select id='svc-cycle' className='mv-select' value={form.billingCycle} onChange={set('billingCycle')}>
                                    {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-cost'>Cost per bill</label>
                                <div className='mv-svc-money'>
                                    <select aria-label='Currency' className='mv-select' value={form.currency} onChange={set('currency')}>
                                        <option value='INR'>₹</option>
                                        <option value='USD'>$</option>
                                    </select>
                                    <input id='svc-cost' className='mv-input' inputMode='decimal' value={form.cost}
                                        onChange={e => setForm(f => ({ ...f, cost: e.target.value.replace(/[^0-9.]/g, '') }))} />
                                </div>
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-auto'>Auto-renew</label>
                                <select id='svc-auto' className='mv-select' value={form.autoRenew} onChange={set('autoRenew')}>
                                    <option value=''>Not set</option>
                                    <option value='true'>On</option>
                                    <option value='false'>Off</option>
                                </select>
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-start'>Started on</label>
                                <input id='svc-start' type='date' className='mv-input' value={form.startedOn} onChange={set('startedOn')} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-renew'>Renews or expires on</label>
                                <input id='svc-renew' type='date' className='mv-input' value={form.renewsOn} onChange={set('renewsOn')} />
                            </div>
                            <div className='mv-field'>
                                <label className='mv-label' htmlFor='svc-free'>Free tier ends</label>
                                <input id='svc-free' type='date' className='mv-input' value={form.freeTierEndsOn} onChange={set('freeTierEndsOn')} />
                            </div>
                            {(editing.limitFields || []).map(f =>
                                <div className='mv-field' key={f.key}>
                                    <label className='mv-label' htmlFor={'svc-limit-' + f.key}>{f.label}</label>
                                    <input id={'svc-limit-' + f.key} className='mv-input' inputMode='decimal' value={form.limits[f.key] || ''}
                                        onChange={setLimit(f.key)} placeholder='Not set' />
                                </div>)}
                            <div className='mv-field mv-field-full'>
                                <label className='mv-label' htmlFor='svc-url'>Dashboard link</label>
                                <input id='svc-url' className='mv-input' type='url' maxLength={300} value={form.dashboardUrl}
                                    onChange={set('dashboardUrl')} placeholder='https://' />
                            </div>
                            <div className='mv-field mv-field-full'>
                                <label className='mv-label' htmlFor='svc-notes'>Notes</label>
                                <textarea id='svc-notes' className='mv-textarea' rows='3' maxLength={1000} value={form.notes}
                                    onChange={set('notes')} placeholder='Account email, who pays, anything to remember' />
                            </div>
                        </div>
                        <p className='mv-field-hint'>
                            Dates within 30 days and usage over 80% of a limit are marked “Needs attention”. Costs in dollars use the
                            rate in Settings, Monitoring.
                            {editing.account && editing.account.updatedAt &&
                                ` Last changed ${formatDate(editing.account.updatedAt.slice(0, 10))}${editing.account.updatedBy ? ' by ' + editing.account.updatedBy : ''}.`}
                        </p>
                    </form>}
            </SidePanel>

            {toast && <Toast message={toast} onDone={() => setToast('')} />}
        </main>
    )
}

export default ServiceMonitor
